/**
 * Quality settings: the missing checks waived on purpose (`waive` in .flow/quality.yaml). A missing check (no tests, no
 * coverage report, no linter, no lockfile) never counts as passing and, for tests, coverage and lockfiles, blocks
 * Publish. Only an explicit waiver, with a reason, lifts it: the person picks the check, the service (or every one) and
 * says why. The waivers are part of the draft: Save writes them with the other settings.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../table/DataTable';
import { Trash } from '@phosphor-icons/react';

export const WAIVE_CHECKS = {
  tests: 'Tests (and their coverage)',
  coverage: 'Coverage report',
  lint: 'Linter',
  vulnerabilities: 'Lockfile (vulnerabilities, licences)',
};

/** The errors of a new waiver: a known check, a service name or none, a reason of at least 3 characters. */
export function waiverErrors(form) {
  const errors = {};
  if (!WAIVE_CHECKS[form.check]) { errors.check = 'Pick the check.'; }
  if (form.service && !/^[\w.-]{1,100}$/.test(form.service.trim())) { errors.service = 'A service name, or empty for every service.'; }
  if ((form.reason || '').trim().length < 3) { errors.reason = 'Say why: waiving a check is an explicit choice.'; }
  return errors;
}

// Each row keeps its place in the list (`index`): the table may show them sorted, Remove takes out the right one.
const WAIVER_COLUMNS = [
  { key: 'check', label: 'Check', width: 200, value: (w) => WAIVE_CHECKS[w.check] || w.check },
  { key: 'service', label: 'Service', width: 180, value: (w) => w.service || 'Every service' },
  { key: 'reason', label: 'Reason', wrap: 2, value: (w) => w.reason },
];

export default function QualityWaivers({ waivers = [], services = [], canEdit = false, onChange }) {
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const add = () => {
    const found = waiverErrors(form);
    setErrors(found);
    if (Object.keys(found).length) { return; }
    onChange([...waivers, { check: form.check, service: form.service.trim() || null, reason: form.reason.trim() }]);
    setForm(null);
  };
  return (
    <section aria-labelledby="quality-waive-title" className="quality-card" data-testid="quality-waivers">
      <div className="quality-pad-head is-row">
        <div>
          <h2 id="quality-waive-title">Waived missing checks</h2>
          <p className="quality-muted">A missing check (no tests, no coverage report, no lockfile) never counts as passing and blocks Publish. Waive one only on purpose, with a reason.</p>
        </div>
        {canEdit && !form && <button className="quality-btn" onClick={() => { setForm({ check: 'tests', service: '', reason: '' }); setErrors({}); }} type="button">Waive a check</button>}
      </div>
      <div className="quality-table-box is-flush">
        <DataTable
          actions={canEdit ? {
            label: 'Actions',
            width: 56,
            render: (w) => <button aria-label={`Remove the waiver of ${w.check}`} className="quality-btn is-sm is-quiet" onClick={() => onChange(waivers.filter((_, i) => i !== w.index))} type="button"><Trash aria-hidden size={13} /></button>,
          } : undefined}
          className="quality-table"
          columns={WAIVER_COLUMNS}
          empty={<span className="quality-muted">Nothing waived: every missing check counts.</span>}
          rowKey={(w) => `${w.check}-${w.service || 'all'}-${w.index}`}
          rowProps={(w) => ({ 'data-testid': `quality-waiver-${w.index}` })}
          rows={waivers.map((w, index) => ({ ...w, index }))}
        />
      </div>
      {form && (
        <div className="quality-pad-foot is-ground quality-waive-form">
          <label>Check
            <select aria-invalid={Boolean(errors.check)} onChange={(e) => setForm({ ...form, check: e.target.value })} value={form.check}>
              {Object.entries(WAIVE_CHECKS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </label>
          <label>Service
            <select aria-invalid={Boolean(errors.service)} onChange={(e) => setForm({ ...form, service: e.target.value })} value={form.service}>
              <option value="">Every service</option>
              {services.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          <label className="quality-grow">Reason
            <input aria-invalid={Boolean(errors.reason)} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why this check does not apply here" value={form.reason} />
          </label>
          {Object.values(errors).map((text) => <p className="quality-error quality-small" key={text} role="alert">{text}</p>)}
          <div className="quality-row">
            <button className="quality-btn" onClick={() => setForm(null)} type="button">Cancel</button>
            <button className="quality-btn is-primary" onClick={add} type="button">Waive</button>
          </div>
        </div>
      )}
    </section>
  );
}

QualityWaivers.propTypes = {
  canEdit: PropTypes.bool,
  // (waivers) => void: the new list, into the draft.
  onChange: PropTypes.func.isRequired,
  services: PropTypes.arrayOf(PropTypes.string),
  waivers: PropTypes.arrayOf(PropTypes.shape({ check: PropTypes.string, reason: PropTypes.string, service: PropTypes.string })),
};
